import { useEffect, useRef, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';
import { downloadReport, useReportView, type DocumentLanguage } from '../../../api/documents';
import { useInspection } from '../../../api/inspections';
import { Breadcrumbs, Button, ButtonLink, InsetText } from '../../../components/idsk';
import { LANGUAGE_AUTONYMS } from '../../../i18n';
import { useAnnouncer } from '../../../layout/Announcer';
import { PageHeading } from '../../../layout/PageHeading';
import { usePageTitle } from '../../../layout/usePageTitle';
import { QueryError } from '../../common/QueryError';
import { LoadingPage } from '../../common/LoadingPage';
import { DocumentLanguageSelect, DownloadButtons, defaultDocumentLanguage } from './DocumentDownloads';
import { DocumentViewer, documentSections } from './DocumentViewer';

const ID_PREFIX = 'report';

/**
 * "Správa z inšpekcie" displayed in the app: the final report of a finished inspection, rendered from the frozen
 * snapshot (the same document model as the PDF/DOCX), with a table of contents, the report language and the
 * downloads. The language is kept in the URL (`?jazyk=en`).
 */
export function ReportViewPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { announce } = useAnnouncer();
  const [params, setParams] = useSearchParams();
  const requested = params.get('jazyk');
  const lang: DocumentLanguage = requested === 'en' || requested === 'sk' ? requested : defaultDocumentLanguage();
  const inspection = useInspection(id);
  const finished = inspection.data?.reportAvailable === true;
  const view = useReportView(finished ? id : undefined, lang);
  const document = view.data?.document;
  usePageTitle([document?.title ?? t('report.title'), inspection.data?.number ?? t('common.loading'), t('inspections.title')]);

  // announce the language once the report in the newly chosen language is shown
  const shownLang = useRef<string | null>(null);
  useEffect(() => {
    if (!document || view.isPlaceholderData) return;
    if (shownLang.current && shownLang.current !== document.lang) {
      announce(t('report.view.languageShown', { language: LANGUAGE_AUTONYMS[document.lang === 'en' ? 'en' : 'sk'] }));
    }
    shownLang.current = document.lang;
  }, [document, view.isPlaceholderData, announce, t]);

  if (inspection.error) return <QueryError error={inspection.error} asPage />;
  if (inspection.isPending) return <LoadingPage />;
  const i = inspection.data;
  const crumbs = [
    { text: t('nav.home'), to: '/' },
    { text: t('inspections.title'), to: '/inspekcie' },
    { text: i.number, to: `/inspekcie/${i.id}` },
    { text: t('report.title') },
  ];

  if (!finished) {
    return (
      <>
        <Breadcrumbs items={crumbs} />
        <PageHeading caption={i.number}>{t('report.title')}</PageHeading>
        <p className="govuk-body">{t('report.notYet')}</p>
        <ButtonLink to={`/inspekcie/${i.id}`} variant="secondary">
          {t('report.view.back')}
        </ButtonLink>
      </>
    );
  }
  if (view.error) return <QueryError error={view.error} asPage />;
  if (!document) return <LoadingPage />;

  const sections = documentSections(document, ID_PREFIX);
  const uiLang = i18n.resolvedLanguage === 'en' ? 'en' : 'sk';
  const goTo = (event: MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    const target = window.document.getElementById(sectionId);
    if (!target) return;
    event.preventDefault();
    target.focus();
    target.scrollIntoView?.({ block: 'start' });
  };

  return (
    <div className="app-report-view">
      <Breadcrumbs items={crumbs} />
      <PageHeading caption={i.number}>
        <span lang={document.lang !== uiLang ? document.lang : undefined}>{document.title}</span>
      </PageHeading>
      <p className="govuk-body">{t('report.view.intro')}</p>

      <div className="app-report-toolbar app-no-print">
        <DocumentLanguageSelect id="report-lang" value={lang} hint={t('report.view.languageHint')} onChange={(l) => setParams({ jazyk: l }, { replace: true })} />
        <DownloadButtons id="report-view" name={t('report.title')} lang={lang} onDownload={(format, l) => downloadReport(i.id, format, l)} />
        <Button variant="secondary" onClick={() => window.print()}>
          {t('report.view.print')}
        </Button>
        <ButtonLink to={`/inspekcie/${i.id}/grafy?jazyk=${lang}`} variant="secondary">
          {t('charts.open')}
        </ButtonLink>
      </div>

      {sections.length > 0 && (
        <nav className="app-report-toc app-no-print" aria-labelledby="report-toc-heading">
          <h2 className="govuk-heading-s" id="report-toc-heading">
            {t('report.view.contents')}
          </h2>
          <ol className="govuk-list govuk-list--number">
            {sections.map((s) => (
              <li key={s.id}>
                <a className="govuk-link" href={`#${s.id}`} lang={s.text.lang ?? (document.lang !== uiLang ? document.lang : undefined)} onClick={(e) => goTo(e, s.id)}>
                  {s.text.value}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      )}

      {view.isPlaceholderData && (
        <InsetText>
          <span role="status">{t('common.loading')}</span>
        </InsetText>
      )}
      <DocumentViewer document={document} idPrefix={ID_PREFIX} />

      <p className="govuk-body app-no-print">
        <ButtonLink to={`/inspekcie/${i.id}`} variant="secondary">
          {t('report.view.back')}
        </ButtonLink>
      </p>
    </div>
  );
}
